/**
 * Palantir PMS - Company Info Settings (Save-on-submit)
 * Autor: Alberto Castro Ull
 */

import React, { useEffect, useRef, useState } from "react";
import { FiBriefcase, FiCheck, FiImage, FiMapPin, FiSave, FiTruck, FiUser } from "react-icons/fi";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { readStoredUser } from "../../../utils/sessionUser";
import { usePurchasingSettingsSection } from "../usePurchasingSettingsSection";
import "../../PurchasingUi.css";
import "./CompanyInfoTab.css";

const COPY = {
  en: {
    denied: "You do not have permission to view company information.", readOnly: "You can view company information. Editing requires permission to update company settings.",
    title: "Company Info", description: "Company details, delivery address and contact information used on purchase orders.",
    company: "Company details", address: "Company address", delivery: "Delivery address", contact: "Purchasing contact",
    footer: "Purchase order footer", footerHint: "Optional note displayed on purchase orders.",
    company_name: "Company name", company_tax_id: "Tax ID (CIF/NIF)", company_phone: "Phone", company_email: "Email",
    logo: "Company logo", logoHint: "PNG, JPEG or WebP, up to 5 MB. The logo is used on purchase orders and uploaded when you save.",
    chooseLogo: "Choose image", replaceLogo: "Replace image", removeLogo: "Remove logo", noLogo: "No logo selected",
    logoAlt: "Company logo preview", logoUnavailable: "Logo preview unavailable", logoLoading: "Loading logo preview…", logoRemoved: "The logo will be removed when you save.",
    logoTypeError: "Choose a PNG, JPEG or WebP image.", logoSizeError: "The image must be 5 MB or smaller.",
    company_address: "Address", company_city: "City", company_zip: "Postal code", company_country: "Country",
    delivery_address: "Address", delivery_city: "City", delivery_zip: "Postal code", delivery_country: "Country",
    purchasing_contact_first_name: "First name", purchasing_contact_last_name: "Last name", purchasing_contact_email: "Email", purchasing_contact_phone: "Phone",
    loading: "Loading company information…", saving: "Saving…", save: "Save changes", cancel: "Discard changes", unsaved: "Unsaved changes",
    saved: "Company information saved.", unchanged: "No unsaved changes", required: "Company name is required.",
    loadError: "Company information could not be loaded. No changes can be saved until it is available.",
    saveError: "Company information could not be saved. Your changes are still here.", retry: "Try again",
    missingUnit: "An active business unit is required to create the company profile.",
  },
  es: {
    denied: "No tienes permiso para consultar los datos de la empresa.", readOnly: "Puedes consultar los datos de la empresa. Para editarlos necesitas permiso para modificar la configuración de empresa.",
    title: "Datos de la empresa", description: "Datos de la empresa, dirección de entrega y contacto utilizados en las órdenes de compra.",
    company: "Datos de la empresa", address: "Dirección de la empresa", delivery: "Dirección de entrega", contact: "Contacto de compras",
    footer: "Pie de la orden de compra", footerHint: "Nota opcional que aparecerá en las órdenes de compra.",
    company_name: "Razón social", company_tax_id: "CIF / NIF", company_phone: "Teléfono", company_email: "Correo electrónico",
    logo: "Logotipo de la empresa", logoHint: "PNG, JPEG o WebP, hasta 5 MB. El logotipo se utiliza en las órdenes de compra y se sube al guardar.",
    chooseLogo: "Seleccionar imagen", replaceLogo: "Reemplazar imagen", removeLogo: "Quitar logotipo", noLogo: "Sin logotipo seleccionado",
    logoAlt: "Vista previa del logotipo de la empresa", logoUnavailable: "Vista previa del logotipo no disponible", logoLoading: "Cargando la vista previa del logotipo…", logoRemoved: "El logotipo se quitará al guardar.",
    logoTypeError: "Selecciona una imagen PNG, JPEG o WebP.", logoSizeError: "La imagen debe ocupar 5 MB o menos.",
    company_address: "Dirección", company_city: "Ciudad", company_zip: "Código postal", company_country: "País",
    delivery_address: "Dirección", delivery_city: "Ciudad", delivery_zip: "Código postal", delivery_country: "País",
    purchasing_contact_first_name: "Nombre", purchasing_contact_last_name: "Apellidos", purchasing_contact_email: "Correo electrónico", purchasing_contact_phone: "Teléfono",
    loading: "Cargando los datos de la empresa…", saving: "Guardando…", save: "Guardar cambios", cancel: "Descartar cambios", unsaved: "Cambios sin guardar",
    saved: "Datos de la empresa guardados.", unchanged: "Sin cambios pendientes", required: "La razón social es obligatoria.",
    loadError: "No se han podido cargar los datos de la empresa. No se podrán guardar cambios hasta que estén disponibles.",
    saveError: "No se han podido guardar los datos de la empresa. Tus cambios se conservan.", retry: "Reintentar",
    missingUnit: "Se necesita una unidad de negocio activa para crear los datos de la empresa.",
  },
};

const LOGO_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);
const MAX_LOGO_BYTES = 5 * 1024 * 1024;
function useLogoPreview({ source, storedLogo, canFetch, businessUnitId, userId }) {
  const scope = JSON.stringify([businessUnitId, userId, storedLogo, source]);
  const [failedSource, setFailedSource] = useState(null);
  const [download, setDownload] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!canFetch) return undefined;
    let cancelled = false;
    let objectUrl;
    const controller = new AbortController();
    const currentSession = () => {
      const user = readStoredUser();
      return Number(user?.active_business_unit_id) === businessUnitId && user?.id === userId;
    };
    setDownload({ scope, status: "loading" });
    // Saved logos are images, not external URLs. Fetch their bytes with the
    // authenticated session; only local object URLs are rendered in the page.
    axios.get("/api/purchasing/company-profile/logo-content", {
      responseType: "blob", signal: controller.signal,
      headers: { "X-Pilcore-Business-Unit-Id": businessUnitId }, pilcoreExpectedUserId: userId,
    }).then(({ data }) => {
      if (cancelled || !currentSession()) return;
      if (!(data instanceof Blob) || !data.size || data.type.split(";")[0] !== "image/png") {
        throw new Error("Invalid company logo response");
      }
      objectUrl = URL.createObjectURL(data);
      setDownload({ scope, status: "ready", url: objectUrl });
    }).catch(() => {
      if (!cancelled && currentSession()) setDownload({ scope, status: "error" });
    });
    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [canFetch, scope, attempt, businessUnitId, userId]);

  const current = download?.scope === scope && canFetch ? download : null;
  const imageSource = source && failedSource !== scope ? source : current?.url || "";
  const failed = current?.status === "error" || (failedSource === scope && !canFetch);
  return {
    source: imageSource, failed, loading: canFetch && !failed && !imageSource,
    retry: () => setAttempt((value) => value + 1),
    onError: () => {
      if (current?.url) setDownload({ scope, status: "error" });
      else setFailedSource(scope);
    },
  };
}

function CompanyField({ name, copy, form, onChange, wide = false, required = false, disabled = false }) {
  const type = name.endsWith("_email") ? "email" : name.endsWith("_phone") ? "tel" : "text";
  return (
    <div className={`company-info-field${wide ? " company-info-field--wide" : ""}`}>
      <label htmlFor={`company-info-${name}`}>{copy[name]}{required && <span aria-hidden="true"> *</span>}</label>
      <input id={`company-info-${name}`} name={name} type={type} className="form-control"
        value={form[name] ?? ""} disabled={disabled} required={required} onChange={(event) => onChange(name, event.target.value)} />
    </div>
  );
}

function CompanySection({ id, icon: Icon, title, children }) {
  return (
    <section className="company-info-section" aria-labelledby={`company-info-${id}-title`}>
      <h2 id={`company-info-${id}-title`}><Icon aria-hidden="true" />{title}</h2>
      <div className="company-info-fields">{children}</div>
    </section>
  );
}

export default function CompanyInfoTab() {
  const { themeId, languageId } = usePmsTheme();
  const copy = COPY[languageId] || COPY.en;
  const { user, businessUnitId, canView, canEdit, context, isCurrent, requestConfig } = usePurchasingSettingsSection("company");
  const [form, setForm] = useState({});
  const [savedForm, setSavedForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileId, setProfileId] = useState(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [loadedContext, setLoadedContext] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [removeLogo, setRemoveLogo] = useState(false);
  const [logoError, setLogoError] = useState("");
  const logoInput = useRef(null);
  const savePending = useRef(false);
  const requestGeneration = useRef(0);
  const dirty = Boolean(logoFile || removeLogo) || JSON.stringify(form) !== JSON.stringify(savedForm);
  const currentProfile = loadedContext === context;
  const canSave = canEdit && dirty && currentProfile && !saving && !loading && !loadFailed && Boolean(profileId || businessUnitId);
  const hasLogo = Boolean(logoFile || (!removeLogo && form.company_logo_url));
  const logoSource = logoFile ? logoPreview : "";
  const canFetchLogo = Boolean(canView && currentProfile && !loading && !loadFailed && hasLogo && !logoFile && !removeLogo && businessUnitId);
  const preview = useLogoPreview({ source: logoSource, storedLogo: form.company_logo_url,
    canFetch: canFetchLogo, businessUnitId, userId: user?.id });

  useEffect(() => {
    if (!logoFile) {
      setLogoPreview("");
      return undefined;
    }
    const preview = URL.createObjectURL(logoFile);
    setLogoPreview(preview);
    return () => URL.revokeObjectURL(preview);
  }, [logoFile]);

  useEffect(() => {
    const generation = ++requestGeneration.current;
    savePending.current = false;
    setSaving(false);
    setLoading(true);
    setLoadFailed(false);
    setSaved(false);
    setError("");
    setLogoFile(null);
    setRemoveLogo(false);
    setLogoError("");
    setForm({}); setSavedForm({}); setProfileId(null); setLoadedContext(null);
    if (!canView || !isCurrent()) { setLoading(false); return undefined; }
    axios.get("/api/purchasing/company-profile", requestConfig).then((response) => {
      if (requestGeneration.current !== generation || !isCurrent()) return;
      const profile = response.data || {};
      setForm(profile);
      setSavedForm(profile);
      setProfileId(profile.id || null);
      setLoadedContext(context);
    }).catch(() => {
      if (requestGeneration.current === generation) setLoadFailed(true);
    }).finally(() => {
      if (requestGeneration.current === generation) setLoading(false);
    });
    return () => { requestGeneration.current += 1; };
  }, [canView, context, isCurrent, requestConfig, retryCount]);

  const handleFieldChange = (name, value) => {
    if (!canEdit || !isCurrent("update")) return;
    setForm((previous) => ({ ...previous, [name]: value }));
    setSaved(false);
    setError("");
  };

  const handleLogoChange = (event) => {
    if (!canEdit || !isCurrent("update")) return;
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!LOGO_TYPES.has(file.type) || !file.size) {
      setLogoError("logoTypeError");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError("logoSizeError");
      return;
    }
    setLogoFile(file);
    setRemoveLogo(false);
    setLogoError("");
    setError("");
    setSaved(false);
  };

  const handleRemoveLogo = () => {
    if (!canEdit || !isCurrent("update")) return;
    setLogoFile(null);
    setRemoveLogo(Boolean(savedForm.company_logo_url));
    setLogoError("");
    setError("");
    setSaved(false);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!canSave || savePending.current || !isCurrent("update")) return;
    if (!String(form.company_name || "").trim()) {
      setError("required");
      return;
    }
    savePending.current = true;
    setSaving(true);
    setError("");
    setSaved(false);
    const generation = requestGeneration.current;
    try {
      const { company_logo_preview_url: _preview, ...profile } = form;
      if (!profileId) profile.business_unit_id = businessUnitId;
      if (removeLogo) profile.remove_logo = true;
      const multipart = Boolean(logoFile || removeLogo);
      let payload = profile;
      if (multipart) {
        payload = new FormData();
        payload.append("profile", JSON.stringify(profile));
        if (logoFile) payload.append("file", logoFile);
      }
      const url = `/api/purchasing/company-profile${profileId ? `/${profileId}` : ""}`;
      const request = profileId ? axios.patch : axios.post;
      const response = multipart
        ? await request(url, payload, { ...requestConfig, headers: { ...requestConfig.headers, "Content-Type": undefined } })
        : await request(url, payload, requestConfig);
      if (requestGeneration.current !== generation || !isCurrent()) return;
      if (!profileId) setProfileId(response.data?.id || null);
      const savedProfile = { ...form };
      if (removeLogo) {
        savedProfile.company_logo_url = null;
      }
      if (Object.prototype.hasOwnProperty.call(response.data || {}, "company_logo_url")) savedProfile.company_logo_url = response.data.company_logo_url;
      delete savedProfile.company_logo_preview_url;
      setForm(savedProfile);
      setSavedForm(savedProfile);
      setLogoFile(null);
      setRemoveLogo(false);
      setLogoError("");
      setSaved(true);
    } catch (saveError) {
      if (requestGeneration.current === generation) setError(saveError?.response?.data?.error || "saveError");
    } finally {
      if (requestGeneration.current === generation) {
        savePending.current = false;
        setSaving(false);
      }
    }
  };

  const field = (name, options = {}) => <CompanyField key={name} name={name} copy={copy} form={form} disabled={!canEdit} onChange={handleFieldChange} {...options} />;

  return (
    <div className="pilcore-purchasing-ui company-info" data-theme={themeId}>
      <header className="company-info-header">
        <h1>{copy.title}</h1>
        <p>{copy.description}</p>
      </header>
      {!canView ? <p role="alert">{copy.denied}</p> : loading || (!loadFailed && !currentProfile) ? <div className="company-info-empty" role="status">{copy.loading}</div> : loadFailed ? (
        <div className="company-info-empty">
          <p className="company-info-error" role="alert">{copy.loadError}</p>
          <button className="company-info-button" type="button" onClick={() => setRetryCount((count) => count + 1)}>{copy.retry}</button>
        </div>
      ) : (
        <form className="company-info-form" onSubmit={handleSave} aria-busy={saving}>
          {!canEdit && <p role="status">{copy.readOnly}</p>}
          <fieldset className="company-info-body" disabled={saving}>
            <div className="company-info-column">
              <CompanySection id="company" icon={FiBriefcase} title={copy.company}>
                {field("company_name", { wide: true, required: true })}
                {field("company_tax_id")}{field("company_phone")}
                {field("company_email", { wide: true })}
                <div className="company-info-field company-info-field--wide company-info-logo">
                  <label htmlFor="company-info-logo-file">{copy.logo}</label>
                  <input id="company-info-logo-file" name="company_logo_file" ref={logoInput} type="file" hidden disabled={!canEdit}
                    accept="image/png,image/jpeg,image/webp" onChange={handleLogoChange}
                    aria-describedby={`company-info-logo-hint${logoError ? " company-info-logo-error" : ""}`}
                    aria-invalid={Boolean(logoError)} />
                  <div className="company-info-logo-content">
                    <div className="company-info-logo-preview">
                      {preview.source ? <img key={preview.source} src={preview.source} alt={copy.logoAlt} onError={preview.onError} /> : <FiImage aria-hidden="true" />}
                    </div>
                    <div className="company-info-logo-details">
                      <p className="company-info-logo-status" role="status">{preview.loading ? copy.logoLoading : preview.failed ? copy.logoUnavailable :
                        logoFile?.name || (removeLogo ? copy.logoRemoved : !preview.source ? hasLogo ? copy.logoUnavailable : copy.noLogo : "")}</p>
                      <div className="company-info-logo-actions">
                        {preview.failed && canFetchLogo && <button type="button" className="company-info-button company-info-logo-retry" onClick={preview.retry}>{copy.retry}</button>}
                        {canEdit && <button type="button" className="company-info-button" onClick={() => { if (isCurrent("update")) logoInput.current?.click(); }}
                          aria-controls="company-info-logo-file" aria-describedby="company-info-logo-hint">
                          {hasLogo ? copy.replaceLogo : copy.chooseLogo}
                        </button>}
                        {canEdit && hasLogo && <button type="button" className="company-info-button company-info-logo-remove" onClick={handleRemoveLogo}>{copy.removeLogo}</button>}
                      </div>
                      <p id="company-info-logo-hint" className="company-info-logo-hint">{copy.logoHint}</p>
                      {logoError && <p id="company-info-logo-error" className="company-info-error" role="alert">{copy[logoError]}</p>}
                    </div>
                  </div>
                </div>
              </CompanySection>
              <CompanySection id="address" icon={FiMapPin} title={copy.address}>
                {field("company_address", { wide: true })}
                {field("company_city")}{field("company_zip")}
                {field("company_country", { wide: true })}
              </CompanySection>
            </div>
            <div className="company-info-column">
              <CompanySection id="delivery" icon={FiTruck} title={copy.delivery}>
                {field("delivery_address", { wide: true })}
                {field("delivery_city")}{field("delivery_zip")}
                {field("delivery_country", { wide: true })}
              </CompanySection>
              <CompanySection id="contact" icon={FiUser} title={copy.contact}>
                {field("purchasing_contact_first_name")}{field("purchasing_contact_last_name")}
                {field("purchasing_contact_email")}{field("purchasing_contact_phone")}
              </CompanySection>
            </div>
            <div className="company-info-footer-note">
              <label htmlFor="company-info-footer-text">{copy.footer}</label>
              <textarea id="company-info-footer-text" name="footer_text" className="form-control" rows={2}
                value={form.footer_text ?? ""} disabled={!canEdit} onChange={(event) => handleFieldChange("footer_text", event.target.value)}
                aria-describedby="company-info-footer-hint" />
              <p id="company-info-footer-hint">{copy.footerHint}</p>
            </div>
          </fieldset>
          <footer className="company-info-actions">
            <div className="company-info-feedback" aria-live="polite">
              {error ? <span className="company-info-error" role="alert">{copy[error] || error}</span> :
                !profileId && !businessUnitId ? <span className="company-info-error" role="alert">{copy.missingUnit}</span> :
                saved ? <span className="company-info-success"><FiCheck aria-hidden="true" />{copy.saved}</span> :
                <span>{saving ? copy.saving : dirty ? copy.unsaved : copy.unchanged}</span>}
            </div>
            {canEdit && <div className="company-info-action-buttons">
              <button type="button" className="company-info-button" disabled={!dirty || saving} onClick={() => {
                setForm(savedForm); setLogoFile(null); setRemoveLogo(false); setLogoError(""); setError(""); setSaved(false);
              }}>{copy.cancel}</button>
              <button type="submit" className="company-info-button company-info-button--primary" disabled={!canSave}>
                <FiSave aria-hidden="true" />{saving ? copy.saving : copy.save}
              </button>
            </div>}
          </footer>
        </form>
      )}
    </div>
  );
}
